import { DOCUMENT } from '@angular/common'
import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { ActivatedRoute } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { Subscription } from 'rxjs'
import { take } from 'rxjs/operators'
import { Sale, SaleActivity, SalePayment, StatusTagColors } from '../../models'
import { InvoiceService } from '../../services/invoice.service'
import { PaymentService } from '../../services/payment.service'

type SaleActivityResponse = SaleActivity[] & {
  metadata?: {
    store_tz?: string
  }
}

@Component({
  selector: 'app-bill-detail',
  templateUrl: './bill-detail.component.html',
  styleUrls: ['./bill-detail.component.scss'],
  standalone: false,
})
export class BillDetailComponent implements OnInit, OnDestroy {
  private static readonly PRINT_ROOT_CLASS = 'bill-detail-print-root'

  public bill: Sale
  public activity: SaleActivityResponse
  public payments: SalePayment[]

  @ViewChild('printHost', { read: ElementRef })
  private printHost?: ElementRef<HTMLElement>
  @ViewChild('printView', { read: ElementRef })
  private printView?: ElementRef<HTMLElement>

  private activityLoaded = false
  private paymentsLoaded = false
  private isPrinting = false
  private destroyed = false
  private readonly dataSubscriptions = new Subscription()
  private printStyleElement?: HTMLStyleElement
  private printTimer?: ReturnType<typeof setTimeout>
  private cleanupPrintHandler?: () => void
  private previousDocumentTitle?: string
  private readonly printDataReady: Promise<void>
  private resolvePrintDataReady!: () => void

  constructor(private activatedRoute: ActivatedRoute,
    private invoiceService: InvoiceService,
    private titleService: Title,
    private translate: TranslateService,
    private paymentService: PaymentService,
    @Inject(DOCUMENT) private document: Document) {
      this.printDataReady = new Promise<void>(resolve => {
        this.resolvePrintDataReady = () => resolve()
      })
      // noop
     }

  public ngOnInit(): void {
    this.bill = this.activatedRoute.snapshot.data.invoice

    this.titleService.setTitle(
      this.translate.instant(
        'ROUTE_TITLE.page_title_template',
        { title: this.translate.instant('SALE_DETAIL.HEADERS.sale', { sale_number: this.bill.sale_number }) }
      )
    )

    this.dataSubscriptions.add(this.invoiceService.significantActivity(this.bill.id).pipe(take(1)).subscribe({
      next: (val: SaleActivity[]) => {
        this.activity = val as SaleActivityResponse
        this.activityLoaded = true
        this.resolvePrintWhenReady()
      },
      error: () => {
        this.activity = [] as SaleActivityResponse
        this.activityLoaded = true
        this.resolvePrintWhenReady()
      },
      complete: () => {
        if (!this.activityLoaded) {
          this.activity = [] as SaleActivityResponse
          this.activityLoaded = true
          this.resolvePrintWhenReady()
        }
      },
    }))

    this.dataSubscriptions.add(this.paymentService.adjustmentCounts(this.bill.id).pipe(take(1)).subscribe({
      next: (val: SalePayment[]) => {
        this.payments = val.sort((a,b) => a.due_date > b.due_date ? -1 : 1)
        this.paymentsLoaded = true
        this.resolvePrintWhenReady()
      },
      error: () => {
        this.payments = []
        this.paymentsLoaded = true
        this.resolvePrintWhenReady()
      },
      complete: () => {
        if (!this.paymentsLoaded) {
          this.payments = []
          this.paymentsLoaded = true
          this.resolvePrintWhenReady()
        }
      },
    }))
  }

  public ngOnDestroy(): void {
    this.destroyed = true
    this.dataSubscriptions.unsubscribe()
    this.resolvePrintDataReady()
    this.cleanupPrint()
  }

  public statusTagColor(order: any): string {
    return `pl-tag--${(StatusTagColors as any)[order.display_status]}`
  }

  public canBePaid(bill: Sale): boolean{
    return Number(bill.amount_outstanding) > 0
  }

  public makePayment(): void {
    this.paymentService.makePayment(this.bill.sale_number)
  }

  public printStatement(): void {
    void this.startPrint()
  }

  public get paymentMethod(): {
    acct_num?: string
    acct_type?: string
    acct_type_display?: string
    name?: string
  } | null {
    return (this.bill as any).payment_plan_payment_method || null
  }

  public get billingAddress(): {
    name?: string
    address_1?: string
    address_2?: string
    city?: string
    state_or_province?: string
    postal_code?: string
    country?: string
  } | null {
    return (this.bill as any).billing_address || null
  }

  public get billingAddressLines(): string[] {
    const address = this.billingAddress
    if (!address) return []

    return [
      address.name,
      address.address_1,
      address.address_2,
      [address.city, address.state_or_province].filter(Boolean).join(', ').trim(),
      address.postal_code,
      address.country,
    ].filter((line): line is string => Boolean(line && line.trim()))
  }

  public get paymentMethodLabel(): string {
    const method = this.paymentMethod
    if (!method) return '--'
    const label = method.acct_type_display || this.getPaymentMethodTypeLabel(method.acct_type) || 'Card'
    return method.acct_num ? `${label} ending in ${method.acct_num}` : label
  }

  public hasBillingDetails(): boolean {
    return Boolean(this.paymentMethod || this.billingAddress)
  }

  public hasActivityNotes(): boolean {
    return Boolean(this.activity?.some(item => item.notes))
  }

  public hasTransactions(): boolean {
    return Boolean(this.activity?.some(item => item.sale_transaction_id != null))
  }

  public formatActivityDate(value: string): string {
    if (!value) return ''

    const date = new Date(value)
    const storeTimeZone = this.activity?.metadata?.store_tz || 'America/Chicago'
    const formatted = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: storeTimeZone,
      timeZoneName: 'short',
    }).format(date)

    return formatted.replace(/\bAM\b/g, 'am').replace(/\bPM\b/g, 'pm')
  }

  private getPaymentMethodTypeLabel(type?: string): string {
    switch (type) {
      case 'credit_card':
        return 'Credit Card'
      case 'bank_account':
      case 'ach':
      case 'echeck':
        return 'Bank Account'
      default:
        return type
          ? type.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
          : ''
    }
  }

  private resolvePrintWhenReady(): void {
    if (this.activityLoaded && this.paymentsLoaded) {
      this.resolvePrintDataReady()
    }
  }

  private async startPrint(): Promise<void> {
    const printHost = this.printHost?.nativeElement
    const printView = this.printView?.nativeElement

    if (this.isPrinting || this.destroyed || !printHost || !printView || typeof window === 'undefined') {
      return
    }

    this.isPrinting = true
    await this.printDataReady
    if (this.destroyed) {
      this.cleanupPrint()
      return
    }
    printView.classList.add(BillDetailComponent.PRINT_ROOT_CLASS)
    this.document.body.insertBefore(printView, this.document.body.firstChild)
    this.ensurePrintStyles()
    this.previousDocumentTitle = this.document.title
    this.document.title = this.translate.instant('BILL_DETAIL.PRINT.title', {
      sale_number: this.bill.sale_number,
    })

    this.cleanupPrintHandler = () => this.cleanupPrint()
    window.addEventListener('afterprint', this.cleanupPrintHandler)
    this.printTimer = setTimeout(() => {
      this.printTimer = undefined
      if (!this.destroyed && this.isPrinting) {
        window.print()
      }
    })
  }

  private ensurePrintStyles(): void {
    if (this.printStyleElement) return

    this.printStyleElement = this.document.createElement('style')
    this.printStyleElement.textContent = `
      @media print {
        html {
          background: #fff !important;
        }

        body { margin: 0; background: #fff; }
        body > *:not(style):not(script):not(.${BillDetailComponent.PRINT_ROOT_CLASS}) {
          display: none !important;
        }
        body > .${BillDetailComponent.PRINT_ROOT_CLASS} { display: block !important; }
      }
    `
    this.document.head.appendChild(this.printStyleElement)
  }

  private cleanupPrint(): void {
    const printHost = this.printHost?.nativeElement
    const printView = this.printView?.nativeElement
    if (this.printTimer !== undefined) {
      clearTimeout(this.printTimer)
      this.printTimer = undefined
    }
    if (this.cleanupPrintHandler && typeof window !== 'undefined') {
      window.removeEventListener('afterprint', this.cleanupPrintHandler)
      this.cleanupPrintHandler = undefined
    }
    this.printStyleElement?.remove()
    this.printStyleElement = undefined
    if (this.previousDocumentTitle !== undefined) {
      this.document.title = this.previousDocumentTitle
      this.previousDocumentTitle = undefined
    }
    if (printHost && printView && this.isPrinting) {
      printView.classList.remove(BillDetailComponent.PRINT_ROOT_CLASS)
      printHost.appendChild(printView)
    }
    this.isPrinting = false
  }

}
